/* eslint-disable no-undef */
/* eslint-disable react/prop-types */
import { createContext, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useEffect } from "react";

export const CareGiverContext = createContext();

let caregiverSessionRedirecting = false;

const CareGiverContextProvider = (props) => {
  const backendUrl = import.meta.env.VITE_BACKEND_URL;

  const [cToken, setCToken] = useState(localStorage.getItem("cToken") || "");
  const [bookings, setBookings] = useState([]);
  const [dashData, setDashData] = useState(false);
  const [profileData, setProfileData] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [applicationPagination, setApplicationPagination] = useState({
    page: 1,
    totalPages: 1,
    limit: 8,
    total: 0,
  });
  const [jobPagination, setJobPagination] = useState({
    page: 1,
    totalPages: 1,
    limit: 8,
    total: 0,
  });
  const [hasActiveWork, setHasActiveWork] = useState(false);
  const [bookingsPagination, setBookingsPagination] = useState({
    page: 1,
    totalPages: 1,
    limit: 5,
  });
  const [loading, setLoading] = useState(false);
  const [notOnboarded, setNotOnboarded] = useState(false);

  //Contract, Payout, and Bank Details
  const [contractData, setContractData] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [contractPagination, setContractPagination] = useState({
    page: 1,
    totalPages: 1,
    limit: 8,
    total: 0,
  });
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [payoutPagination, setPayoutPagination] = useState({
    page: 1,
    totalPages: 1,
    limit: 8,
    total: 0,
  });
  const [bankDetails, setBankDetails] = useState({
    bankCode: "",
    bankAccountNumber: "",
    bankAccountName: "",
    subaccountStatus: null,
  });

  // Save the token to localStorage whenever it changes
  useEffect(() => {
    if (cToken) {
      localStorage.setItem("cToken", cToken);
    } else {
      localStorage.removeItem("cToken");
    }
  }, [cToken]);

  // Expired caregiver sessions must leave protected pages immediately.
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        const isCaregiverRequest = url.includes("/api/caregiver/");
        const isAuthRequest = url.includes("/api/caregiver/auth/");

        if (
          status === 401 &&
          isCaregiverRequest &&
          !isAuthRequest &&
          !caregiverSessionRedirecting
        ) {
          caregiverSessionRedirecting = true;
          localStorage.removeItem("cToken");
          setCToken("");
          toast.error("Session expired. Please log in again.");
          window.location.replace("/login");
        }

        return Promise.reject(error);
      },
    );

    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  // fetch profile ONCE when token exists
  useEffect(() => {
    if (cToken) getProfileData();
  }, [cToken]);

  // Center check for onboarding status based on database profile vetting status
  useEffect(() => {
    if (profileData) {
      setNotOnboarded(profileData.vetting_status !== "Onboarded");
    }
  }, [profileData]);

  // CareGiver booking data
  const getBookings = async ({
    page = 1,
    limit = 5,
    status = "All Statuses",
    search = "",
  } = {}) => {
    try {
      setLoading(true);

      const { data } = await axios.get(
        `${backendUrl}/api/caregiver/bookings`,
        {
          headers: { cToken },
          params: { page, limit, status, search },
        },
      );

      if (data.success) {
        setBookings(data.data || []);
        setBookingsPagination(
          data.pagination || {
            page: data.page || page,
            totalPages: data.pages || 1,
            limit,
            total: data.total || 0,
          }
        );
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  // CareGiver profile
  const getProfileData = async () => {
    try {
      const { data } = await axios.get(backendUrl + "/api/caregiver/profile", {
        headers: { cToken },
      });
      if (data.success && data.profileData) {
        setProfileData(data.profileData);
        
        // Also populate bankDetails from the same profile data to avoid duplicate API calls
        const profile = data.profileData;
        setBankDetails({
          bankCode: profile.bankCode || "",
          bankAccountNumber: profile.bankAccountNumber || "",
          bankAccountName: profile.bankAccountName || "",
          subaccountStatus: profile.chapaSubaccountStatus || null,
          paymentAccountReady: profile.paymentAccountReady ?? false,
        });
      }
    } catch (error) {
      console.log(error);
      toast.error(error.message);
    }
  };

  //  cancel caregiver booking
  const cancelBooking = async (bookingId, reason) => {
    try {
      const { data } = await axios.patch(
        `${backendUrl}/api/caregiver/bookings/${bookingId}/cancel`,
        { reason },
        { headers: { cToken } },
      );

      if (data.success) {
        toast.success(data.message || "Booking Cancelled");
        getBookings();
        getDashData();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error(error.message);
      console.log(error);
    }
  };

  //  mark booking completed
  const completeBooking = async (bookingId) => {
    try {
      const { data } = await axios.patch(
        `${backendUrl}/api/caregiver/bookings/${bookingId}/complete`,
        {},
        { headers: { cToken } },
      );

      if (data.success) {
        toast.success(data.message || "Booking Completed");
        getBookings();
        getDashData();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error(error.message);
      console.log(error);
    }
  };

  //CareGiver dashboard
  const getDashData = async () => {
    try {
      const { data } = await axios.get(
        backendUrl + "/api/caregiver/dashboard",
        { headers: { cToken } },
      );

      if (data.success) {
        setDashData(data.dashData);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.log(error);
      toast.error(error.message);
    }
  };

  const getJobs = async ({
    page = 1,
    limit = 5,
    search = "",
    subcity = "",
    workTime = "",
    experienceLevel = "",
  } = {}) => {
    try {
      setLoading(true);

      const { data } = await axios.get(`${backendUrl}/api/caregiver/jobs`, {
        params: {
          page,
          limit,
          search,
          subcity,
          workTime,
          experienceLevel,
        },
        headers: { cToken },
      });

      if (data.success) {
        setJobs(data.jobs);
        setHasActiveWork(data.hasActiveWork);
        setJobPagination(data.pagination);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  const applyToJob = async (jobId) => {
    try {
      const { data } = await axios.post(
        `${backendUrl}/api/caregiver/jobs/apply`,
        { jobId },
        { headers: { cToken } },
      );

      if (data.success) {
        toast.success(data.message);
        // REFRESH LIST IMMEDIATELY
        getJobs({ page: jobPagination.page });
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      const backendMessage = error.response?.data?.message;
      let message = backendMessage || error.message || "Failed to apply to job";

      if (backendMessage?.includes("assigned to an active job")) {
        message =
          "You can't apply to this job because you're already assigned to another active job.";
      } else if (backendMessage?.includes("active booking")) {
        message =
          "You can't apply to this job because you're already working on another booking.";
      }

      toast.error(message);
    }
  };

  const fetchApplicationStatus = useCallback(
    async ({
      page = 1,
      limit = 8,
      status = "all",
      search = "",
      subcity = "all",
    }) => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          `${backendUrl}/api/caregiver/jobs/applications`,
          {
            headers: { cToken },
            params: { page, limit, status, search, subcity },
          },
        );

        if (data.success) {
          setApplications(data.applications);
          setApplicationPagination(data.pagination);
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        toast.error(error.response?.data?.message || error.message);
      } finally {
        setLoading(false);
      }
    },
    [backendUrl, cToken],
  );

  // Helper methods for consistent UI display
  const getWorkStateLabel = (workState) => {
    const labels = {
      pending: "Pending",
      assigned: "Assigned",
      "work-started": "Work Started",
      completed: "Completed",
      cancelled: "Cancelled",
    };
    return labels[workState] || workState;
  };

  const getWorkStateColor = (workState) => {
    const colors = {
      pending: "bg-yellow-500 text-white",
      assigned: "bg-blue-600 text-white",
      "work_started": "bg-emerald-700 text-white",
      completed: "bg-emerald-500 text-white",
      cancelled: "bg-red-600 text-white",
    };
    return colors[workState] || "bg-gray-400 text-white";
  };

  const getApplicationStatusColor = (status) => {
    const normalizedStatus = status?.toLowerCase();
    const colors = {
      pending:
        "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
      "under-review":
        "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
      accepted:
        "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
      rejected: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    };
    return (
      colors[normalizedStatus] ||
      "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400"
    );
  };
  const getApplicationStatusLabel = (status) => {
    const labels = {
      pending: "Pending",
      "under-review": "Under Review",
      accepted: "Accepted",
      rejected: "Rejected",
      withdrawn: "Withdrawn",
    };
    return labels[status?.toLowerCase()] || status;
  };

  // Change caregiver password
  const changePassword = async (
    currentPassword,
    newPassword,
    confirmPassword,
  ) => {
    try {
      const { data } = await axios.post(
        `${backendUrl}/api/caregiver/profile/change-password`,
        {
          careGiverId: profileData.id,
          currentPassword,
          newPassword,
          confirmPassword,
        },
        { headers: { cToken } },
      );

      if (data.success) {
        toast.success(data.message || "Password changed successfully");
        return { success: true };
      } else {
        toast.error(data.message || "Failed to change password");
        return { success: false, message: data.message };
      }
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      toast.error(msg);
      return { success: false, message: msg };
    }
  };

  // Contract Management Functions
  const getContractData = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${backendUrl}/api/caregiver/contracts`,
        { headers: { cToken } },
      );

      if (data.success) {
        setContractData(data.contract || null);
        return data.contract;
      } else {
        setContractData(null);
        return null;
      }
    } catch (error) {
      console.error("Error fetching contract data:", error);
      setContractData(null);
      return null;
    }
  }, [backendUrl, cToken]);

  const fetchContracts = useCallback(
    async ({ page = 1, limit = 8, status = "all" } = {}) => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          `${backendUrl}/api/caregiver/contracts`,
          {
            params: { page, limit, status },
            headers: { cToken },
          },
        );

        if (data.success) {
          setContracts(data.contracts || []);
          setContractPagination(
            data.pagination || {
              page,
              limit,
              total: data.total || 0,
              totalPages: data.pages || 1,
            },
          );
          if (data.contract !== undefined) setContractData(data.contract);
          return data.contracts || [];
        }
        toast.error(data.message || "Failed to fetch contracts");
        return [];
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to fetch contracts",
        );
        return [];
      } finally {
        setLoading(false);
      }
    },
    [backendUrl, cToken],
  );

  // Payout History Functions
  const getPayoutHistory = useCallback(
    async (filters = {}) => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          `${backendUrl}/api/caregiver/payouts`,
          {
            params: filters,
            headers: { cToken },
          },
        );

        if (data.success) {
          // Map periodStart to month for compatibility with dashboard display
          const payouts = (data.payouts || []).map((payout) => ({
            ...payout,
            month: payout.periodStart,
          }));
          setPayoutHistory(payouts);
          setPayoutPagination({
            page: data.page || filters.page || 1,
            limit: filters.limit || 8,
            total: data.total || 0,
            totalPages: data.pages || 1,
          });
          return payouts;
        } else {
          toast.error(data.message || "Failed to fetch payout history");
          return [];
        }
      } catch (error) {
        console.error("Error fetching payout history:", error);
        toast.error(
          error.response?.data?.message || "Failed to fetch payout history",
        );
        return [];
      } finally {
        setLoading(false);
      }
    },
    [backendUrl, cToken],
  );

  // Bank Details Functions
  const getBankDetails = useCallback(async () => {
    try {
      const { data } = await axios.get(`${backendUrl}/api/caregiver/profile`, {
        headers: { cToken },
      });

      if (data.success && data.profileData) {
        const profile = data.profileData;
        const details = {
          bankCode: profile.bankCode || "",
          bankAccountNumber: profile.bankAccountNumber || "",
          bankAccountName: profile.bankAccountName || "",
          // Backend spreads the Prisma model which uses `chapaSubaccountStatus`,
          // NOT `subaccountStatus` — the old key caused this to always be null.
          subaccountStatus: profile.chapaSubaccountStatus || null,
          paymentAccountReady: profile.paymentAccountReady ?? false,
        };
        setBankDetails(details);
        return details;
      }
      return null;
    } catch (error) {
      console.error("Error fetching bank details:", error);
      return null;
    }
  }, [backendUrl, cToken]);

  const updateBankDetails = useCallback(
    async (bankData, showToast = true) => {
      try {
        const { data } = await axios.put(
          `${backendUrl}/api/caregiver/profile/bank`,
          {
            bankCode: bankData.bankCode,
            bankAccountNumber: bankData.bankAccountNumber,
            bankAccountName: bankData.bankAccountName,
          },
          { headers: { cToken } },
        );

        if (data.success) {
          if (showToast) {
            toast.success("Bank details updated successfully");
          }
          await getBankDetails();
          await getProfileData();
          return {
            success: true,
            needsSubaccountCreation: data.needsSubaccountCreation,
            subaccountResult: data.subaccountResult,
          };
        } else {
          toast.error(data.message || "Failed to update bank details");
          return { success: false, message: data.message };
        }
      } catch (error) {
        const msg =
          error.response?.data?.message || "Failed to update bank details";
        toast.error(msg);
        return { success: false, message: msg };
      }
    },
    [backendUrl, cToken, getBankDetails, getProfileData],
  );

  /**
   * saveBankAndActivate — single-action function:
   * 1. PUT /api/caregiver/profile/bank  — save bank details
   * 2. POST /api/caregiver/profile/subaccount — create Chapa subaccount (fallback)
   * Returns { success, subaccountCreated, error }
   */
  const saveBankAndActivate = useCallback(
    async (bankData) => {
      // Step 1: save bank details
      const bankResult = await updateBankDetails(bankData, false);
      if (!bankResult.success) {
        return {
          success: false,
          subaccountCreated: false,
          error: bankResult.message,
        };
      }

      // If subaccount was successfully created/activated during updateBankDetails (backend handles this)
      if (bankResult.subaccountResult?.status === "success") {
        toast.success("Bank details saved and payment account activated successfully!");
        await getBankDetails();
        await getProfileData();
        return { success: true, subaccountCreated: true };
      }

      // Step 2 fallback: create Chapa subaccount if it is not active yet
      try {
        const { data } = await axios.post(
          `${backendUrl}/api/caregiver/profile/subaccount`,
          {},
          { headers: { cToken } },
        );

        if (data.success) {
          toast.success(
            data.message || "Bank details saved and payment account activated!",
          );
          await getBankDetails();
          await getProfileData();
          return { success: true, subaccountCreated: true };
        } else {
          // Bank saved but subaccount failed
          toast.error(
            data.message ||
              "Bank details saved but payment account activation failed. Please retry.",
          );
          await getBankDetails();
          return {
            success: false,
            subaccountCreated: false,
            error: data.message,
          };
        }
      } catch (error) {
        const msg =
          error.response?.data?.message ||
          "Bank details saved but payment account activation failed. Please retry.";
        toast.error(msg);
        await getBankDetails();
        return { success: false, subaccountCreated: false, error: msg };
      }
    },
    [backendUrl, cToken, updateBankDetails, getBankDetails, getProfileData],
  );

  const createSubaccount = useCallback(async () => {
    try {
      const { data } = await axios.post(
        `${backendUrl}/api/caregiver/profile/subaccount`,
        {},
        { headers: { cToken } },
      );

      if (data.success) {
        toast.success(data.message || "Payment account activated successfully");
        // Refresh bank details and profile
        await getBankDetails();
        await getProfileData();
        return { success: true, data: data.subaccount };
      } else {
        toast.error(data.message || "Failed to activate payment account");
        return { success: false, message: data.message };
      }
    } catch (error) {
      const msg =
        error.response?.data?.message || "Failed to activate payment account";
      toast.error(msg);
      return { success: false, message: msg };
    }
  }, [backendUrl, cToken, getBankDetails]);

  const getSubaccountStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${backendUrl}/api/caregiver/profile/subaccount/status`,
        { headers: { cToken } },
      );

      if (data.success) {
        return data.status;
      }
      return null;
    } catch (error) {
      console.error("Error fetching subaccount status:", error);
      return null;
    }
  }, [backendUrl, cToken]);

  const value = {
    cToken,
    setCToken,
    backendUrl,
    bookings,
    bookingsPagination,
    loading,
    getBookings,
    cancelBooking,
    completeBooking,

    // Legacy mappings for backwards compatibility during transition
    appointments: bookings,
    appointmentPagination: bookingsPagination,
    setAppointmentPagination: setBookingsPagination,
    getAppointments: getBookings,
    cancelAppointment: cancelBooking,
    completeAppointment: completeBooking,
    dashData,
    getDashData,
    profileData,
    setProfileData,
    getProfileData,
    applyToJob,
    getJobs,
    jobs,
    hasActiveWork,
    notOnboarded,
    jobPagination,
    setJobPagination,
    fetchApplicationStatus,
    applications,
    applicationPagination,
    changePassword,
    //  Contract, Payout, and Bank Details
    contractData,
    getContractData,
    contracts,
    contractPagination,
    fetchContracts,
    payoutHistory,
    payoutPagination,
    getPayoutHistory,
    bankDetails,
    getBankDetails,
    updateBankDetails,
    saveBankAndActivate,
    createSubaccount,
    getSubaccountStatus,

    // Helper methods
    getWorkStateLabel,
    getWorkStateColor,
    getApplicationStatusColor,
    getApplicationStatusLabel,
  };

  return (
    <CareGiverContext.Provider value={value}>
      {props.children}
    </CareGiverContext.Provider>
  );
};

export default CareGiverContextProvider;
